import "./FormEditor.less";

import { TextLine } from "features/common/TextLine";
import { BannerType, FormBanner } from "features/qualityForm/common/FormBanner";
import { useStrings } from "features/i18n/useStrings";
import { useMemo, useState } from "react";

import {
    GuxButton,
    GuxFormFieldTextarea,
    GuxFormFieldTextLike,
    GuxIcon,
    GuxTooltip,
    GuxTruncate,
} from "genesys-spark-components-react";

import {
    qualityFormActions,
    selectCanEditForm,
    selectResourceCenterUri,
    selectEnableReportingViewRedirects,
} from "../qualityFormSlice";
import {
    selectForm,
    selectFormType,
    selectIsAutomatedEvaluationForm,
} from "../qualityFormSlices/qualityFormCommons";
import {
    selectIsQuestionCountInvalid,
    selectIsFormNameLengthy,
    selectIsFormNameEmpty,
    selectIsQuestionCountAtOrAboveLimit,
} from "../qualityFormValidations";
import { DefaultLimits } from "constants/DefaultLimits";
import strings from "./FormEditor.strings";
import { QuestionGroupList } from "./questionGroup/QuestionGroupList";
import FormType from "enums/FormType";
import { ActionIcon, DeleteButton } from "features/common/ActionIcons";
import { I18nComponent } from "react/utils/I18nComponent";
import { openInNewTab } from "features/common/Utils";
import { DateTime } from "features/i18n/DateTime";
import { Card } from "features/common/Card";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectIsFormNameDuplicate } from "../qualityFormValidations";
import { ErrorStatement } from "./common/ErrorStatement/ErrorStatement";
import { selectIsInCopyingViewMode } from "./copyFormSlice";

type markdownRole = "header" | "footer";
type FormEditorProps = {
    formType?: FormType;
};

function MarkDownHeaderComponent() {
    const markdown = useAppSelector((state) => selectForm(state)?.header);
    const canEditForm = useAppSelector(selectCanEditForm);
    const dispatch = useAppDispatch();
    const handlers = useMemo(
        () =>
            canEditForm
                ? {
                      onChange: (value: string) =>
                          dispatch(qualityFormActions.updateHeader(value)),
                      onDelete: () =>
                          dispatch(qualityFormActions.clearHeader()),
                      onAdd: () =>
                          dispatch(qualityFormActions.updateHeader("")),
                  }
                : {},
        [dispatch, canEditForm]
    );
    return (
        <MarkDownTextComponent
            title="header"
            {...handlers}
            markdown={markdown}
        />
    );
}
function MarkDownFooterComponent() {
    const markdown = useAppSelector((state) => selectForm(state)?.footer);
    const canEditForm = useAppSelector(selectCanEditForm);
    const dispatch = useAppDispatch();
    const handlers = useMemo(
        () =>
            canEditForm
                ? {
                      onChange: (value: string) =>
                          dispatch(qualityFormActions.updateFooter(value)),
                      onDelete: () =>
                          dispatch(qualityFormActions.clearFooter()),
                      onAdd: () =>
                          dispatch(qualityFormActions.updateFooter("")),
                  }
                : {},
        [dispatch, canEditForm]
    );
    return (
        <MarkDownTextComponent
            title="footer"
            {...handlers}
            markdown={markdown}
        />
    );
}

type MarkDownTextComponentProps = {
    title: markdownRole;
    markdown?: string;
    onDelete?: () => void;
    onChange?: (markdown: string) => void;
    onAdd?: () => void;
};

function MarkDownTextComponent({
    title,
    markdown,
    onChange,
    onDelete,
    onAdd,
}: MarkDownTextComponentProps) {
    const t = useStrings(strings);
    const hasMarkdownText = markdown !== undefined;
    const [showMarkdownSupport, setShowMarkdownSupport] = useState(false);
    const resourceCenterUri = useAppSelector(selectResourceCenterUri);
    return (
        <div className="MarkDownTextComponent">
            <TextLine>{t.markdown.labels.section[title]}</TextLine>
            <Card className="filled">
                {hasMarkdownText ? (
                    <Card>
                        <GuxFormFieldTextarea
                            resize="auto"
                            labelPosition="above"
                        >
                            <label slot="label">
                                <span>{t.markdown.labels[title]}</span>
                                {onDelete && (
                                    <DeleteButton onDelete={onDelete} />
                                )}
                            </label>
                            <textarea
                                onFocus={() => {
                                    setShowMarkdownSupport(true);
                                }}
                                onBlur={() => {
                                    setShowMarkdownSupport(false);
                                }}
                                defaultValue={markdown}
                                readOnly={!onChange}
                                slot="input"
                                name="textarea"
                                onChange={({ currentTarget }) => {
                                    onChange && onChange(currentTarget.value);
                                }}
                            ></textarea>
                        </GuxFormFieldTextarea>
                        <div className="markdown-hint">
                            {showMarkdownSupport && (
                                <I18nComponent
                                    i18nKey="FormEditor.markdown.hint"
                                    substitutions={{
                                        Strong: <strong></strong>,
                                    }}
                                ></I18nComponent>
                            )}
                            <ActionIcon
                                iconName="fa/circle-question-regular"
                                text={t.markdown.help}
                                performAction={() => {
                                    openInNewTab(resourceCenterUri);
                                }}
                            ></ActionIcon>
                        </div>
                    </Card>
                ) : (
                    onAdd && (
                        <GuxButton
                            className="add-markdown-button"
                            accent="secondary"
                            onClick={onAdd}
                        >
                            <GuxIcon iconName="add" decorative={true}></GuxIcon>
                            <span>
                                {t.markdown.labels.addMarkdownEditor[title]}
                            </span>
                        </GuxButton>
                    )
                )}
            </Card>
        </div>
    );
}

export function FormHeader() {
    const t = useStrings(strings);

    const formType = useAppSelector(selectFormType);

    const formName = useAppSelector((state) => {
        return selectForm(state)?.name ?? "";
    });

    const canViewEvaluationFormLevelViewButton = useAppSelector(
        selectEnableReportingViewRedirects
    );

    return (
        <div>
            <FormMetaData />
            {formType &&
                formType !== FormType.ASSESSMENT &&
                (formName.trim() === "" ? (
                    <div className="invalid-input-placeholder">
                        <h1>
                            {
                                t.FormNameEditor.labels.defaultFormNameText[
                                    formType
                                ]
                            }
                        </h1>
                        <GuxIcon
                            iconName="fa/hexagon-exclamation-solid"
                            screenreaderText={
                                t.FormNameEditor.labels.invalidInput
                            }
                        ></GuxIcon>
                    </div>
                ) : (
                    <GuxTruncate maxLines={1}>
                        <div className="form-header">
                            <h1 className="gux-heading-lg-bold">{formName}</h1>
                            {canViewEvaluationFormLevelViewButton && (
                                <GuxButton
                                    aria-label={t.FormNameEditor.labels.icon}
                                >
                                    {t.FormNameEditor.labels.icon}
                                </GuxButton>
                            )}
                        </div>
                    </GuxTruncate>
                ))}
        </div>
    );
}

export function FormEditorHeader() {
    const t = useStrings(strings);

    const formType = useAppSelector(selectFormType);

    const formName = useAppSelector((state) => selectForm(state)?.name ?? "");

    const errorString = useAppSelector((state) => {
        return selectIsFormNameEmpty(state)
            ? t.FormNameEditor.errors.empty
            : selectIsFormNameLengthy(state)
            ? t.FormNameEditor.errors.lengthyName({
                  maxFormNameLength: DefaultLimits.maxFormNameLength,
              })
            : selectIsFormNameDuplicate(state)
            ? t.FormNameEditor.errors.duplicate
            : undefined;
    });

    const dispatch = useAppDispatch();

    const canViewPerformanceViewButton = useAppSelector(
        selectEnableReportingViewRedirects
    );

    const isAutomatedEvaluationForm =
        useAppSelector(selectIsAutomatedEvaluationForm) &&
        formType === FormType.EVALUATION;

    return (
        <>
            <FormMetaData />
            {(formType === FormType.EVALUATION ||
                formType === FormType.SURVEY) && (
                <>
                    <div className="form-header" data-testid="form-header">
                        <h1 className="gux-heading-lg-bold">
                            {isAutomatedEvaluationForm
                                ? t.FormNameEditor.labels.title
                                      .automatedEvaluation
                                : t.FormNameEditor.labels.title[formType]}
                        </h1>
                        {canViewPerformanceViewButton && (
                            <GuxButton
                                aria-label={t.FormNameEditor.labels.icon}
                            >
                                {t.FormNameEditor.labels.icon}
                            </GuxButton>
                        )}
                    </div>
                    <GuxFormFieldTextLike
                        label-position="above"
                        className="input-field"
                    >
                        <input
                            slot="input"
                            name="name"
                            type="text"
                            value={formName}
                            required
                            onChange={(event) =>
                                dispatch(
                                    qualityFormActions.changeFormName(
                                        event.target.value
                                    )
                                )
                            }
                        />
                        <label slot="label">
                            {formType && t.FormNameEditor.labels[formType]}
                        </label>

                        {errorString && (
                            <span slot="error" role="alert">
                                {errorString}
                            </span>
                        )}
                    </GuxFormFieldTextLike>
                </>
            )}
        </>
    );
}

export function FormEditor({ formType }: FormEditorProps) {
    const t = useStrings(strings);

    const canEditForm = useAppSelector((state) => {
        return selectCanEditForm(state);
    });
    const hasInsufficientScorableQuestions = useAppSelector((state) =>
        selectIsQuestionCountInvalid(state)
    );
    const isQuestionCountAtOrAboveLimit = useAppSelector(
        selectIsQuestionCountAtOrAboveLimit
    );
    const dispatch = useAppDispatch();

    return (
        <div className="FormEditor">
            {canEditForm && isQuestionCountAtOrAboveLimit && (
                <FormBanner type={BannerType.ERROR}>
                    {t.labels.questionLimitReached({ maxQuestionCount: 200 })}
                </FormBanner>
            )}
            {canEditForm ? <FormEditorHeader /> : <FormHeader />}
            {formType === FormType.SURVEY && <MarkDownHeaderComponent />}
            {hasInsufficientScorableQuestions && (
                <ErrorStatement
                    errorMessage={t.Form.errors.minimumScorableQuestions}
                />
            )}
            <QuestionGroupList />
            {canEditForm && (
                <div className="add-question-group">
                    <GuxButton
                        onClick={() => {
                            dispatch(qualityFormActions.addNewQuestionGroup());
                        }}
                        disabled={isQuestionCountAtOrAboveLimit || undefined}
                        aria-disabled={
                            isQuestionCountAtOrAboveLimit || undefined
                        }
                        data-testid="add-question-group"
                    >
                        <GuxIcon icon-name="add" decorative />
                        <span>{t.labels.addQuestionGroup}</span>
                    </GuxButton>
                    {isQuestionCountAtOrAboveLimit && (
                        <GuxTooltip>
                            {t.labels.questionLimitReachedTooltip({
                                maxQuestionCount:
                                    DefaultLimits.maxQuestionCount,
                            })}
                        </GuxTooltip>
                    )}
                </div>
            )}
            {formType === FormType.SURVEY && <MarkDownFooterComponent />}
            <TextLine>{t.labels.endOfForm}</TextLine>
        </div>
    );
}

export function FormMetaData() {
    const t = useStrings(strings);

    const formType = useAppSelector(selectFormType);

    const modifiedDate = useAppSelector((state) => {
        return selectForm(state)?.modifiedDate;
    });

    const isPublishedForm = useAppSelector((state) => {
        return selectForm(state)?.published ?? false;
    });

    const isFormDisabled = useAppSelector(
        (state) => selectForm(state)?.disabled ?? false
    );

    const isInCopyingViewMode = useAppSelector(selectIsInCopyingViewMode);

    return (
        <div className="FormMetaData">
            {(isPublishedForm || isFormDisabled) &&
            (formType === FormType.EVALUATION ||
                formType === FormType.SURVEY) ? (
                <FormBanner
                    type={isFormDisabled ? BannerType.ERROR : BannerType.INFO}
                >
                    <span data-testid="form-banner-with-date" dir="auto">
                        {modifiedDate ? (
                            <I18nComponent
                                i18nKey={
                                    isFormDisabled
                                        ? "FormEditor.labels.disabledFormWithDate"
                                        : "FormEditor.labels.publishedFormWithDate"
                                }
                                substitutions={{
                                    date: (
                                        <strong dir="ltr">
                                            <DateTime date={modifiedDate} />
                                        </strong>
                                    ),
                                }}
                            />
                        ) : isFormDisabled ? (
                            t.labels.disabledForm
                        ) : (
                            t.labels.publishedForm
                        )}
                    </span>
                </FormBanner>
            ) : !isInCopyingViewMode && modifiedDate ? (
                <div
                    className="FormHeaderMetadataEntry"
                    data-testid="form-header-metadata-entry"
                >
                    <span className="gux-label">{t.labels.modifiedDate}</span>
                    <span className="value">
                        <DateTime date={modifiedDate} />
                    </span>
                </div>
            ) : (
                <></>
            )}
        </div>
    );
}
